/**
 * Admin page: review saved user experience feedback (summary + list + detail).
 *
 * Route: `/admin/feedback`
 * Requires authentication: Yes (admin only)
 *
 * @module AdminFeedbackPage
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchAdminFeedbackDetail,
  fetchAdminFeedbackList,
  fetchAdminFeedbackSummary,
  type AdminFeedbackDetail,
  type AdminFeedbackListItem,
  type AdminFeedbackSummary,
} from '@/lib/adminFeedbackApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';

const PAGE_SIZE = 20;

function BreakdownList({
  title,
  items,
  emptyLabel,
}: Readonly<{
  title: string;
  items: readonly { label: string; count: number }[];
  emptyLabel: string;
}>) {
  if (!items.length) {
    return (
      <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
        <p className="font-ui text-sm font-medium text-foreground">{title}</p>
        <p className="font-ui mt-1 text-xs text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
      <p className="font-ui text-sm font-medium text-foreground">{title}</p>
      <ul className="font-ui mt-2 max-h-36 space-y-1 overflow-y-auto text-xs text-muted-foreground">
        {items.map((row) => (
          <li key={`${title}-${row.label}`} className="flex justify-between gap-2">
            <span className="truncate text-foreground">{row.label}</span>
            <span className="shrink-0 tabular-nums">{row.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DetailField({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <p className="font-ui text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-ui mt-1 whitespace-pre-wrap text-sm text-foreground">{value}</p>
    </div>
  );
}

export default function AdminFeedbackPage() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<AdminFeedbackSummary | null>(null);
  const [list, setList] = useState<AdminFeedbackListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    const data = await fetchAdminFeedbackSummary();
    setSummary(data);
  }, []);

  const loadList = useCallback(async (p: number) => {
    setListLoading(true);
    const data = await fetchAdminFeedbackList(p, PAGE_SIZE);
    if (data) {
      setList([...data.items]);
      setTotal(data.total);
      setPage(data.page);
      setTotalPages(data.totalPages);
    }
    setListLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadSummary();
      await loadList(1);
      if (!cancelled) {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList, loadSummary]);

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    const d = await fetchAdminFeedbackDetail(id);
    setDetail(d);
    setDetailLoading(false);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
        <span className="sr-only font-ui">{t('admin.feedbackReview.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-ui text-2xl font-bold text-foreground">
          {t('admin.feedbackReview.title')}
        </h2>
        <p className="font-ui mt-1 text-muted-foreground">{t('admin.feedbackReview.subtitle')}</p>
      </div>

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardDescription className="font-ui">
                {t('admin.feedbackReview.summary.total')}
              </CardDescription>
              <CardTitle className="font-ui text-3xl tabular-nums">{summary.totalCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardDescription className="font-ui">
                {t('admin.feedbackReview.summary.last7Days')}
              </CardDescription>
              <CardTitle className="font-ui text-3xl tabular-nums">
                {summary.last7DaysCount}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardDescription className="font-ui">
                {t('admin.feedbackReview.summary.last30Days')}
              </CardDescription>
              <CardTitle className="font-ui text-3xl tabular-nums">
                {summary.last30DaysCount}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardDescription className="font-ui">
                {t('admin.feedbackReview.summary.contactConsent')}
              </CardDescription>
              <CardTitle className="font-ui text-3xl tabular-nums">
                {summary.contactConsentCount}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {summary && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="border-border/70">
            <CardHeader>
              <CardTitle className="font-ui text-lg">{t('admin.feedbackReview.ratings.title')}</CardTitle>
              <CardDescription className="font-ui">
                {t('admin.feedbackReview.ratings.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="font-ui grid gap-2 text-sm">
              <p className="text-foreground">
                <span className="text-muted-foreground">
                  {t('admin.feedbackReview.ratings.overall')}:
                </span>{' '}
                {summary.avgOverallRating ?? '—'}
              </p>
              <p className="text-foreground">
                <span className="text-muted-foreground">
                  {t('admin.feedbackReview.ratings.goalSupport')}:
                </span>{' '}
                {summary.avgGoalSupportRating ?? '—'}
              </p>
            </CardContent>
          </Card>
          <div className="grid gap-4 sm:grid-cols-1">
            <BreakdownList
              title={t('admin.feedbackReview.breakdown.usageFrequency')}
              items={summary.usageFrequencyBreakdown}
              emptyLabel={t('admin.feedbackReview.breakdown.empty')}
            />
            <BreakdownList
              title={t('admin.feedbackReview.breakdown.primaryArea')}
              items={summary.primaryAreaBreakdown}
              emptyLabel={t('admin.feedbackReview.breakdown.empty')}
            />
            <BreakdownList
              title={t('admin.feedbackReview.breakdown.device')}
              items={summary.deviceContextBreakdown}
              emptyLabel={t('admin.feedbackReview.breakdown.empty')}
            />
          </div>
        </div>
      )}

      <Card className="border-border/70">
        <CardHeader>
          <CardTitle className="font-ui text-lg">{t('admin.feedbackReview.table.title')}</CardTitle>
          <CardDescription className="font-ui">
            {t('admin.feedbackReview.table.description', { total })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            </div>
          ) : list.length === 0 ? (
            <p className="font-ui py-8 text-center text-sm text-muted-foreground">
              {t('admin.feedbackReview.table.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-ui">ID</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.date')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.overall')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.goal')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.usage')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.area')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.device')}</TableHead>
                    <TableHead className="font-ui">{t('admin.feedbackReview.table.contact')}</TableHead>
                    <TableHead className="font-ui w-[100px]">
                      {t('admin.feedbackReview.table.actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-ui tabular-nums">{row.id}</TableCell>
                      <TableCell className="font-ui whitespace-nowrap text-xs">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="font-ui">{row.overallRating}</TableCell>
                      <TableCell className="font-ui">{row.goalSupportRating}</TableCell>
                      <TableCell className="font-ui max-w-[140px] truncate text-xs">
                        {row.usageFrequency}
                      </TableCell>
                      <TableCell className="font-ui max-w-[140px] truncate text-xs">
                        {row.primaryArea}
                      </TableCell>
                      <TableCell className="font-ui text-xs">{row.deviceContext}</TableCell>
                      <TableCell className="font-ui text-xs">
                        {row.contactConsent ? t('admin.feedbackReview.table.yes') : t('admin.feedbackReview.table.no')}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="font-ui"
                          onClick={() => openDetail(row.id)}
                        >
                          {t('admin.feedbackReview.table.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <p className="font-ui text-xs text-muted-foreground">
                {t('admin.feedbackReview.pagination.page', { page, totalPages })}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="font-ui"
                  disabled={page <= 1 || listLoading}
                  onClick={() => loadList(page - 1)}
                >
                  {t('admin.feedbackReview.pagination.previous')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="font-ui"
                  disabled={page >= totalPages || listLoading}
                  onClick={() => loadList(page + 1)}
                >
                  {t('admin.feedbackReview.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-ui">
              {t('admin.feedbackReview.detail.title', { id: detail?.id ?? '—' })}
            </DialogTitle>
            <DialogDescription className="font-ui">
              {t('admin.feedbackReview.detail.description')}
            </DialogDescription>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
            </div>
          )}
          {!detailLoading && detail && (
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailField label={t('admin.feedbackReview.detail.submittedAt')} value={formatDate(detail.createdAt)} />
              <DetailField
                label={t('admin.feedbackReview.detail.user')}
                value={
                  detail.username || detail.email || detail.userId || t('admin.feedbackReview.detail.anonymous')
                }
              />
              <DetailField
                label={t('admin.feedbackReview.detail.email')}
                value={detail.email ?? '—'}
              />
              <DetailField
                label={t('admin.feedbackReview.detail.userId')}
                value={detail.userId ?? '—'}
              />
              <DetailField
                label={t('admin.feedbackReview.detail.overallRating')}
                value={String(detail.overallRating)}
              />
              <DetailField
                label={t('admin.feedbackReview.detail.goalRating')}
                value={String(detail.goalSupportRating)}
              />
              <DetailField label={t('admin.feedbackReview.detail.usage')} value={detail.usageFrequency} />
              <DetailField label={t('admin.feedbackReview.detail.primaryArea')} value={detail.primaryArea} />
              <DetailField label={t('admin.feedbackReview.detail.device')} value={detail.deviceContext} />
              <DetailField
                label={t('admin.feedbackReview.detail.usedAreas')}
                value={detail.usedAreas.join(', ') || '—'}
              />
              <DetailField
                label={t('admin.feedbackReview.detail.improvementAreas')}
                value={detail.improvementAreas.join(', ') || '—'}
              />
              <div className="sm:col-span-2">
                <DetailField label={t('admin.feedbackReview.detail.likedMost')} value={detail.likedMost} />
              </div>
              <div className="sm:col-span-2">
                <DetailField
                  label={t('admin.feedbackReview.detail.experience')}
                  value={detail.experienceDetails}
                />
              </div>
              <div className="sm:col-span-2">
                <DetailField label={t('admin.feedbackReview.detail.suggestions')} value={detail.suggestions} />
              </div>
              <DetailField
                label={t('admin.feedbackReview.detail.contactConsent')}
                value={
                  detail.contactConsent
                    ? t('admin.feedbackReview.table.yes')
                    : t('admin.feedbackReview.table.no')
                }
              />
              <DetailField
                label={t('admin.feedbackReview.detail.contactEmail')}
                value={detail.contactEmail ?? '—'}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
